import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import AdminHeader from '../components/AdminHeader';
import VisualPlacementEditor from '../components/VisualPlacementEditor';
import { API_BASE_URL, TEMPLATE_BASE_URL } from '../config/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const authAxios = {
  get: (url, config = {}) => axios.get(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  put: (url, data, config = {}) => axios.put(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  post: (url, data, config = {}) => axios.post(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } })
};

function MockupEditorPage() {
  const { formatId, mockupIndex } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [format, setFormat] = useState(null);
  const [mockup, setMockup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [currentOrientation, setCurrentOrientation] = useState('horizontal');
  const [placements, setPlacements] = useState([]);

  // Helper function to ensure horizontal and vertical objects are independent
  const ensureIndependentOrientations = (mockupData) => {
    if (!mockupData) return mockupData;
    
    const normalized = { ...mockupData };
    
    // Ensure horizontal and vertical are separate objects (not shared references)
    if (normalized.horizontal) {
      normalized.horizontal = { ...normalized.horizontal };
    } else {
      normalized.horizontal = {
        template_file_path: '',
        template_layer: 'behind',
        background: '#ffffff'
      };
    }
    
    if (normalized.vertical) {
      normalized.vertical = { ...normalized.vertical };
    } else {
      normalized.vertical = {
        template_file_path: '',
        template_layer: 'behind',
        background: '#ffffff'
      };
    }
    
    return normalized;
  };

  useEffect(() => {
    document.title = 'Edit Mockup - Admin';
    
    // Check if mockup data was passed via navigation state (for new mockups)
    if (location.state?.mockup && location.state?.format) {
      // Use the passed data for new mockups
      setFormat(location.state.format);
      const normalizedMockup = ensureIndependentOrientations(location.state.mockup);
      setMockup(normalizedMockup);
      // Normalize placements to ensure horizontal and vertical are independent
      const normalizedPlacements = (normalizedMockup.placements || []).map(placement => ({
        ...placement,
        horizontal: placement.horizontal ? { 
          x_offset: placement.horizontal.x_offset ?? 0,
          y_offset: placement.horizontal.y_offset ?? 0,
          design_width: placement.horizontal.design_width ?? placement.design_width ?? 240,
          design_height: placement.horizontal.design_height ?? placement.design_height ?? 240,
          rotation: placement.horizontal.rotation ?? placement.rotation ?? 0
        } : { 
          x_offset: 0, 
          y_offset: 0,
          design_width: placement.design_width ?? 240,
          design_height: placement.design_height ?? 240,
          rotation: placement.rotation ?? 0
        },
        vertical: placement.vertical ? {
          x_offset: placement.vertical.x_offset ?? 0,
          y_offset: placement.vertical.y_offset ?? 0,
          design_width: placement.vertical.design_width ?? placement.design_width ?? 240,
          design_height: placement.vertical.design_height ?? placement.design_height ?? 240,
          rotation: placement.vertical.rotation ?? placement.rotation ?? 0
        } : { 
          x_offset: 0, 
          y_offset: 0,
          design_width: placement.design_width ?? 240,
          design_height: placement.design_height ?? 240,
          rotation: placement.rotation ?? 0
        }
      }));
      setPlacements(normalizedPlacements);
      setLoading(false);
    } else {
      // Fetch from server for existing mockups
      fetchFormat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchFormat runs when formatId/mockupIndex change
  }, [formatId, mockupIndex, location.state]);

  const fetchFormat = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authAxios.get(`${API_BASE_URL}/admin/formats/${formatId}`);
      if (response.data.success) {
        const formatData = response.data.data;
        setFormat(formatData);
        
        const index = parseInt(mockupIndex);
        if (formatData.mockups && formatData.mockups[index]) {
          const mockupData = formatData.mockups[index];
          // Ensure horizontal and vertical are independent objects
          const normalizedMockup = ensureIndependentOrientations(mockupData);
          setMockup(normalizedMockup);
          // Normalize placements to ensure horizontal and vertical are independent
          const normalizedPlacements = (normalizedMockup.placements || []).map(placement => ({
            ...placement,
            horizontal: placement.horizontal ? { 
              x_offset: placement.horizontal.x_offset ?? 0,
              y_offset: placement.horizontal.y_offset ?? 0,
              design_width: placement.horizontal.design_width ?? placement.design_width ?? 240,
              design_height: placement.horizontal.design_height ?? placement.design_height ?? 240,
              rotation: placement.horizontal.rotation ?? placement.rotation ?? 0
            } : { 
              x_offset: 0, 
              y_offset: 0,
              design_width: placement.design_width ?? 240,
              design_height: placement.design_height ?? 240,
              rotation: placement.rotation ?? 0
            },
            vertical: placement.vertical ? {
              x_offset: placement.vertical.x_offset ?? 0,
              y_offset: placement.vertical.y_offset ?? 0,
              design_width: placement.vertical.design_width ?? placement.design_width ?? 240,
              design_height: placement.vertical.design_height ?? placement.design_height ?? 240,
              rotation: placement.vertical.rotation ?? placement.rotation ?? 0
            } : { 
              x_offset: 0, 
              y_offset: 0,
              design_width: placement.design_width ?? 240,
              design_height: placement.design_height ?? 240,
              rotation: placement.rotation ?? 0
            }
          }));
          setPlacements(normalizedPlacements);
        } else {
          setError('Mockup not found');
        }
      } else {
        setError(response.data.error || 'Failed to load format');
      }
    } catch (err) {
      console.error('Error fetching format:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load format');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Update the mockup in the format's mockups array
      // Ensure horizontal and vertical remain independent when saving
      const updatedMockup = {
        ...mockup,
        horizontal: mockup.horizontal ? { ...mockup.horizontal } : {},
        vertical: mockup.vertical ? { ...mockup.vertical } : {},
        placements: placements
      };

      // Ensure mockups array exists
      const currentMockups = format.mockups || [];
      const updatedMockups = [...currentMockups];
      const index = parseInt(mockupIndex);
      
      // If this is a new mockup (index >= current length), add it; otherwise update it
      if (index >= currentMockups.length) {
        updatedMockups.push(updatedMockup);
      } else {
        updatedMockups[index] = updatedMockup;
      }

      const updatedFormat = {
        ...format,
        mockups: updatedMockups
      };

      const response = await authAxios.put(
        `${API_BASE_URL}/admin/formats/${formatId}`,
        updatedFormat
      );

      if (response.data.success) {
        // Navigate back to format editor with mockups tab open
        navigate(`/admin/formats?edit=${formatId}&tab=mockups`);
      } else {
        setError(response.data.error || 'Failed to save mockup');
      }
    } catch (err) {
      console.error('Error saving mockup:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save mockup');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/admin/formats?edit=${formatId}&tab=mockups`);
  };

  const updateMockupField = (field, value) => {
    setMockup(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateOrientationField = (field, value) => {
    setMockup(prev => {
      if (!prev) return prev;
      
      // Create a deep copy to ensure horizontal and vertical are independent
      const updated = { ...prev };
      
      // Always create new separate objects for horizontal and vertical
      // This ensures they never share references
      updated.horizontal = prev.horizontal ? { ...prev.horizontal } : {
        template_file_path: '',
        template_layer: 'behind',
        background: '#ffffff'
      };
      
      updated.vertical = prev.vertical ? { ...prev.vertical } : {
        template_file_path: '',
        template_layer: 'behind',
        background: '#ffffff'
      };
      
      // Only update the current orientation's field - create a new object
      updated[currentOrientation] = {
        ...updated[currentOrientation],
        [field]: value
      };
      
      return updated;
    });
  };

  const getTemplateImageUrl = (orientation) => {
    const templatePath = mockup?.[orientation]?.template_file_path;
    if (!templatePath) return null;
    
    if (templatePath.startsWith('./data/assets/templates/')) {
      const filename = templatePath.replace('./data/assets/templates/', '');
      return `${TEMPLATE_BASE_URL}/${filename}`;
    }
    if (templatePath.startsWith('http://') || templatePath.startsWith('https://')) {
      return templatePath;
    }
    if (!templatePath.includes('/')) {
      return `${TEMPLATE_BASE_URL}/${templatePath}`;
    }
    return templatePath;
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading mockup...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !mockup) {
    return (
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <AdminHeader />
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-6">
            <p className="text-red-800">{error}</p>
            <button
              onClick={handleCancel}
              className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!mockup) {
    return null;
  }

  return (
    <div className="min-h-screen py-8 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <AdminHeader />

        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Edit Mockup: {mockup.name || `Mockup ${parseInt(mockupIndex) + 1}`}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Format: {format?.name || formatId}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md p-6">
          {/* Mockup Name */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Mockup Name</label>
            <input
              type="text"
              value={mockup.name || ''}
              onChange={(e) => updateMockupField('name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Orientation Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              {['horizontal', 'vertical'].map((orientation) => (
                <button
                  key={orientation}
                  onClick={() => setCurrentOrientation(orientation)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                    currentOrientation === orientation
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {orientation}
                </button>
              ))}
            </nav>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Mockup Configuration */}
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900">Template Configuration ({currentOrientation})</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Template File Path</label>
                <input
                  type="text"
                  value={mockup[currentOrientation]?.template_file_path || ''}
                  onChange={(e) => updateOrientationField('template_file_path', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Template Layer</label>
                <select
                  value={mockup[currentOrientation]?.template_layer || 'behind'}
                  onChange={(e) => updateOrientationField('template_layer', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="behind">Behind Design</option>
                  <option value="front">In Front of Design</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Background Color</label>
                <input
                  type="text"
                  value={mockup[currentOrientation]?.background || '#ffffff'}
                  onChange={(e) => updateOrientationField('background', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Output Type</label>
                  <select
                    value={mockup.output_type || 'jpeg'}
                    onChange={(e) => updateMockupField('output_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="jpeg">JPEG</option>
                    <option value="png">PNG</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Output Quality</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={mockup.output_quality || 100}
                    onChange={(e) => updateMockupField('output_quality', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Template Preview */}
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900">Template Preview ({currentOrientation})</h4>
              <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                {getTemplateImageUrl(currentOrientation) ? (
                  <img
                    src={getTemplateImageUrl(currentOrientation)}
                    alt={`${currentOrientation} template`}
                    className="w-full h-auto rounded-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className="w-full h-64 flex items-center justify-center text-gray-400 text-sm"
                  style={{ display: getTemplateImageUrl(currentOrientation) ? 'none' : 'flex' }}
                >
                  No template image available
                </div>
              </div>
            </div>
          </div>

          {/* Placements Editor */}
          <div className="mt-8 border-t border-gray-200 pt-6">
            <h4 className="font-medium text-gray-900 mb-4">Design Placements</h4>
            <VisualPlacementEditor
              placements={placements}
              onPlacementsChange={setPlacements}
              templateImageUrl={getTemplateImageUrl(currentOrientation)}
              orientation={currentOrientation}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default MockupEditorPage;

